/**
 * Firewall Rules Tab
 *
 * Manages nftables firewall rules on the Cloud Gateway agent.
 * Rules are pass-through: stored on the agent, not in the database.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ShieldIcon,
  PlusIcon,
  Trash2Icon,
  PencilIcon,
  Loader2Icon,
  AlertTriangleIcon,
  RefreshCwIcon,
  XIcon,
  InfoIcon,
  CheckCircleIcon,
  LockIcon,
} from 'lucide-react'
import { useState, useEffect } from 'react'

import { gatewayApi, getFirewallRuleOriginLabel } from '@nebula/shared/services'
import type { GatewayFirewallRuleCreate, GatewayFirewallRuleUpdate } from '@nebula/shared/services'
import { getApiErrorText } from '@nebula/shared/utils'

/** Validate IP or CIDR notation (e.g., 192.168.1.1 or 10.0.0.0/24). Returns error message or null. */
function validateIpOrCidr(value: string): string | null {
  if (!value) return null // empty is ok (optional field)
  const pattern = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/
  if (!pattern.test(value))
    return 'Formato inválido. Use IP (ex: 192.168.1.1) ou CIDR (ex: 10.0.0.0/24)'
  const ipPart = value.split('/')[0]
  for (const octet of ipPart.split('.')) {
    if (parseInt(octet) > 255) return `Octeto inválido: ${octet} (máximo 255)`
  }
  if (value.includes('/')) {
    const prefix = parseInt(value.split('/')[1])
    if (prefix > 32) return `Prefixo CIDR inválido: /${prefix} (máximo /32)`
  }
  return null
}

export default function FirewallRulesTab() {
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editingRule, setEditingRule] = useState<any>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [gatewayError, setGatewayError] = useState<string | null>(null)

  // Form state
  const [touchedSource, setTouchedSource] = useState(false)
  const [touchedDestination, setTouchedDestination] = useState(false)
  const [direction, setDirection] = useState('input')
  const [action, setAction] = useState('drop')
  const [protocol, setProtocol] = useState('')
  const [source, setSource] = useState('')
  const [destination, setDestination] = useState('')
  const [port, setPort] = useState('')
  const [comment, setComment] = useState('')

  const {
    data: rulesResult,
    isLoading,
    refetch,
    error: queryError,
  } = useQuery({
    queryKey: ['gateway-firewall-rules'],
    queryFn: gatewayApi.getFirewallRules,
  })

  // Auto-dismiss messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 4000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  useEffect(() => {
    if (gatewayError) {
      const timer = setTimeout(() => setGatewayError(null), 10000)
      return () => clearTimeout(timer)
    }
  }, [gatewayError])

  /**
   * A mutação devolve o RECURSO (não o envelope `{success, result}`). O dual-write grava no
   * banco e tenta aplicar no gateway: se o agent falhar, a resposta vem 200 com `sync_error`
   * preenchido — dizer "aplicada com sucesso" aí seria mentir (a regra existe no banco e NÃO
   * no nftables). Ver BUG-018.
   */
  const checkGatewayResult = (res: any): { ok: boolean; error?: string } => {
    if (res?.sync_error) {
      return { ok: false, error: res.sync_error }
    }
    // Outer level: gRPC/relay communication
    if (res?.success === false) {
      return { ok: false, error: res.error || 'Falha na comunicação com o gateway' }
    }
    // Inner level: nftables operation on the agent
    const inner = res?.result
    if (inner && typeof inner === 'object' && inner.success === false) {
      return { ok: false, error: inner.error || 'O gateway não conseguiu aplicar a regra' }
    }
    return { ok: true }
  }

  const addMutation = useMutation({
    mutationFn: (data: GatewayFirewallRuleCreate) => gatewayApi.addFirewallRule(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['gateway-firewall-rules'] })
      const check = checkGatewayResult(res)
      if (check.ok) {
        setSuccessMessage('Regra aplicada com sucesso')
        resetForm()
      } else {
        setGatewayError(`Erro ao aplicar regra no gateway: ${check.error}`)
      }
    },
  })

  const editMutation = useMutation({
    mutationFn: ({ ruleId, data }: { ruleId: string; data: GatewayFirewallRuleUpdate }) =>
      gatewayApi.updateFirewallRule(ruleId, data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['gateway-firewall-rules'] })
      const check = checkGatewayResult(res)
      if (check.ok) {
        setSuccessMessage('Regra atualizada com sucesso')
        resetForm()
      } else {
        setGatewayError(`Erro ao atualizar regra no gateway: ${check.error}`)
      }
    },
  })

  const removeMutation = useMutation({
    mutationFn: (ruleId: string) => gatewayApi.removeFirewallRule(ruleId),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['gateway-firewall-rules'] })
      const check = checkGatewayResult(res)
      if (check.ok) {
        setSuccessMessage('Regra removida com sucesso')
      } else {
        setGatewayError(`Erro ao remover regra no gateway: ${check.error}`)
      }
    },
    // Sem isto, uma recusa da API some sem deixar rastro — botão que não faz nada. Importa
    // agora porque o backend passou a recusar (409) a remoção de regra gerenciada, e backend
    // e bundle deployam separados (Fly x Pages): na janela entre os dois, o cliente ainda vê
    // os botões numa regra que o servidor já protege.
    onError: (e) => {
      setGatewayError(getApiErrorText(e) || 'Erro ao remover regra')
    },
  })

  const resetForm = () => {
    setShowForm(false)
    setEditingRule(null)
    setDirection('input')
    setAction('drop')
    setProtocol('')
    setSource('')
    setDestination('')
    setPort('')
    setComment('')
    setTouchedSource(false)
    setTouchedDestination(false)
  }

  const startEdit = (rule: any) => {
    setEditingRule(rule)
    setDirection(rule.direction || 'input')
    setAction(rule.action || 'drop')
    setProtocol(rule.protocol && rule.protocol !== 'any' ? rule.protocol : '')
    setSource(rule.source || '')
    setDestination(rule.destination || '')
    setPort(rule.port ? String(rule.port) : '')
    setComment(rule.comment || '')
    setShowForm(true)
  }

  const sourceError = validateIpOrCidr(source)
  const destinationError = validateIpOrCidr(destination)
  // `tcp dport 80` já traz o protocolo embutido; nem "Qualquer" nem ICMP casam com porta
  // — `any dport 80` e `icmp dport 80` não são regras nft que existam. A régua do gateway
  // é `protocol not in ('tcp','udp')` (`nftables_manager._montar_nft_rule`); testar só
  // "protocolo vazio" deixaria ICMP+porta passar, ir ao backend e voltar com `sync_error`
  // — a mesma classe aberta pela metade, com a mensagem na tela dizendo a regra que o
  // código não aplica. Antes da #925 o agent assumia tcp em silêncio e entregava uma
  // regra diferente da pedida, com selo de aplicada.
  const protocolError =
    port && !['tcp', 'udp'].includes(protocol) ? 'Escolha TCP ou UDP para filtrar por porta' : null
  const hasValidationErrors = !!sourceError || !!destinationError || !!protocolError

  const handleSubmit = () => {
    if (hasValidationErrors) return
    if (editingRule) {
      // Edit mode
      const data: GatewayFirewallRuleUpdate = {}
      if (direction !== (editingRule.direction || '')) data.direction = direction
      if (action !== (editingRule.action || '')) data.action = action
      // `undefined` some do JSON, e o backend faz `model_dump(exclude_none=True)` + `if
      // field in payload`: trocar TCP por "Qualquer" NÃO limpava o protocolo — a tela
      // dizia "atualizada com sucesso" e a regra continuava tcp no gateway. Mandar `any`
      // explícito é o mesmo valor que o `to_agent_payload` usa para protocolo nulo.
      // `any` e ausência são o MESMO valor; comparar cru marcaria a regra "Qualquer"
      // como alterada a cada abertura do modal.
      const protocoloAtual =
        editingRule.protocol && editingRule.protocol !== 'any' ? editingRule.protocol : ''
      if (protocol !== protocoloAtual) data.protocol = protocol || 'any'
      // `null`, não `undefined`, pela MESMA razão do `port` abaixo: apagar a Origem
      // querendo bloquear qualquer origem mandava `undefined`, o campo sumia do JSON, o
      // `exclude_unset` mantinha o valor antigo e a tela dizia "atualizada com sucesso"
      // sobre uma política que o gateway não tem.
      if (source !== (editingRule.source || '')) data.source = source || null
      if (destination !== (editingRule.destination || '')) data.destination = destination || null
      // `null` explícito, não `undefined`: o endpoint passou a usar `exclude_unset`, e
      // `undefined` some do JSON — o campo ficaria "não enviado" e o backend manteria a
      // porta antiga. Como o guard acima obriga limpar a porta para escolher
      // "Qualquer", sem isto a única rota da UI para "Qualquer" produzia
      // `protocol=any` + `port=80` — a combinação que o gateway recusa, deixando a
      // regra pendente com `sync_error` para sempre.
      const portNum = port ? parseInt(port, 10) : null
      if (portNum !== (editingRule.port ?? null)) data.port = portNum
      if (comment !== (editingRule.comment || '')) data.comment = comment || null

      editMutation.mutate({ ruleId: editingRule.id, data })
    } else {
      // Add mode
      const payload: GatewayFirewallRuleCreate = {
        direction,
        action,
      }
      if (protocol) payload.protocol = protocol
      if (source) payload.source = source
      if (destination) payload.destination = destination
      if (port) payload.port = parseInt(port, 10)
      if (comment) payload.comment = comment

      addMutation.mutate(payload)
    }
  }

  const isPending = addMutation.isPending || editMutation.isPending
  const isError = addMutation.isError || editMutation.isError
  const error = addMutation.error || editMutation.error

  // Filter out DNAT rules (shown in PortForwardsTab)
  // `{rules, total}` — plano. Ler `result.rules` devolvia undefined e a aba ficava vazia
  // mesmo com regras no gateway (BUG-018).
  const allRules = rulesResult?.rules || []
  const rules = allRules.filter((r: any) => r.type !== 'dnat')

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div className="flex items-center space-x-2">
          <ShieldIcon className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Regras de Firewall</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            disabled={isLoading}
            className="inline-flex items-center rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
          >
            <RefreshCwIcon className={`mr-1.5 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
          <button
            onClick={() => {
              resetForm()
              setShowForm(true)
            }}
            className="inline-flex items-center rounded-lg bg-purple-600 px-3 py-1.5 text-sm text-white transition-colors hover:bg-purple-700"
          >
            <PlusIcon className="mr-1.5 h-4 w-4" />
            Adicionar
          </button>
        </div>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3">
        <InfoIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-500" />
        <div className="space-y-1 text-xs text-blue-700">
          <p>
            <strong>Comportamento padrão:</strong> Tráfego <strong>LAN → WAN é permitido</strong>{' '}
            (saída para internet). Tráfego <strong>WAN → LAN é bloqueado</strong> (exceto
            port-forwards e conexões já estabelecidas).
          </p>
          <p>
            Regras customizadas são avaliadas <strong>antes</strong> da permissão padrão — use
            regras de <em>drop</em> para bloquear tráfego específico de saída.
          </p>
        </div>
      </div>

      {/* Success feedback */}
      {successMessage && (
        <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">
          <CheckCircleIcon className="h-4 w-4 flex-shrink-0 text-green-500" />
          {successMessage}
        </div>
      )}

      {/* Gateway error feedback */}
      {gatewayError && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />
          <div>
            <p className="font-medium">Falha ao aplicar no gateway</p>
            <p className="mt-1 text-xs">{gatewayError}</p>
          </div>
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <div className="space-y-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-900">
              {editingRule ? 'Editar Regra' : 'Nova Regra'}
            </h4>
            <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
              <XIcon className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Direção</label>
              <select
                value={direction}
                onChange={(e) => setDirection(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
              >
                <option value="input">Input</option>
                <option value="output">Output</option>
                <option value="forward">Forward</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Ação</label>
              <select
                value={action}
                onChange={(e) => setAction(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
              >
                <option value="accept">Accept</option>
                <option value="drop">Drop</option>
                <option value="reject">Reject</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Protocolo</label>
              <select
                value={protocol}
                onChange={(e) => setProtocol(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Qualquer</option>
                <option value="tcp">TCP</option>
                <option value="udp">UDP</option>
                <option value="icmp">ICMP</option>
              </select>
              {protocolError && <p className="mt-1 text-xs text-red-600">{protocolError}</p>}
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Porta</label>
              <input
                type="number"
                value={port}
                onChange={(e) => setPort(e.target.value)}
                placeholder="Ex: 80"
                min={1}
                max={65535}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Origem</label>
              <input
                type="text"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                onBlur={() => setTouchedSource(true)}
                placeholder="Ex: 192.168.1.0/24"
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500 ${touchedSource && sourceError ? 'border-red-400' : 'border-gray-300'}`}
              />
              {touchedSource && sourceError && (
                <p className="mt-1 text-xs text-red-500">{sourceError}</p>
              )}
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Destino</label>
              <input
                type="text"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                onBlur={() => setTouchedDestination(true)}
                placeholder="Ex: 10.0.0.0/24"
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500 ${touchedDestination && destinationError ? 'border-red-400' : 'border-gray-300'}`}
              />
              {touchedDestination && destinationError && (
                <p className="mt-1 text-xs text-red-500">{destinationError}</p>
              )}
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-medium text-gray-600">Comentário</label>
              <input
                type="text"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Descrição da regra"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
          {isError && (
            <div className="text-sm text-red-600">
              {/* `getApiErrorText`, não `error.message` cru: numa recusa de validação
                  (422) o `message` é sempre o genérico "Request validation failed", e o
                  motivo — "comentário não pode conter aspas duplas" — vive em
                  `details[].msg`. Validar na entrada foi a decisão certa, mas trocou um
                  `sync_error` que a tela EXIBIA por um 422 que ela não sabia ler. */}
              {getApiErrorText(error) || 'Erro ao salvar regra'}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button
              onClick={resetForm}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-100"
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={isPending || hasValidationErrors}
              className="inline-flex items-center rounded-lg bg-purple-600 px-3 py-1.5 text-sm text-white transition-colors hover:bg-purple-700 disabled:opacity-50"
            >
              {isPending && <Loader2Icon className="mr-1.5 h-4 w-4 animate-spin" />}
              Aplicar
            </button>
          </div>
        </div>
      )}

      {/* Rules Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2Icon className="h-6 w-6 animate-spin text-purple-600" />
        </div>
      ) : queryError ? (
        /* A falha de carregamento vem da própria query (o contrato novo não traz `error`). */
        <div className="flex items-center space-x-2 rounded-lg border border-red-200 bg-red-50 p-4">
          <AlertTriangleIcon className="h-5 w-5 flex-shrink-0 text-red-500" />
          <span className="text-sm text-red-700">
            Não foi possível carregar as regras de firewall.
          </span>
        </div>
      ) : rules.length === 0 ? (
        <div className="py-12 text-center text-gray-500">
          <ShieldIcon className="mx-auto mb-3 h-10 w-10 text-gray-300" />
          <p className="text-sm">Nenhuma regra de firewall configurada.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Direção
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Ação
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Proto
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Origem
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Destino
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Porta
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Comentário
                </th>
                <th className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule: any, index: number) => {
                // Uma chamada por linha: as três leituras anteriores podiam divergir numa
                // edição futura, e a decisão (selo x botões) tem de sair de UMA fonte.
                const origemDaRegra = getFirewallRuleOriginLabel(rule)
                return (
                  <tr
                    key={rule.id || rule.handle || index}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                        {rule.direction || rule.chain || '-'}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${
                          rule.action === 'accept'
                            ? 'bg-green-100 text-green-700'
                            : rule.action === 'drop'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        {rule.action || '-'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      {rule.protocol && rule.protocol !== 'any' ? rule.protocol : '*'}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-700">
                      {rule.source || '*'}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-700">
                      {rule.destination || '*'}
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      {rule.port || rule.port_range || '*'}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500">{rule.comment || ''}</td>
                    <td className="px-3 py-2 text-right">
                      {/*
                      Regra aberta por uma feature (#955) mostra a ORIGEM no lugar dos botões,
                      em vez de botões desabilitados: ela é read-only total, e o caminho para
                      fechar a porta é desligar a feature. Oferecer um botão que só pode
                      devolver 409 seria botão quebrado.
                    */}
                      {origemDaRegra ? (
                        <span
                          className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700"
                          title={`Regra mantida automaticamente (${origemDaRegra}). Para fechá-la, desligue a funcionalidade que a abriu.`}
                        >
                          <LockIcon className="mr-1 h-3 w-3" />
                          {origemDaRegra}
                        </span>
                      ) : (
                        (rule.id || rule.handle) && (
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => startEdit(rule)}
                              className="text-gray-400 transition-colors hover:text-purple-600"
                              title="Editar regra"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => removeMutation.mutate(String(rule.id || rule.handle))}
                              disabled={removeMutation.isPending}
                              className="text-red-400 transition-colors hover:text-red-600"
                              title="Remover regra"
                            >
                              <Trash2Icon className="h-4 w-4" />
                            </button>
                          </div>
                        )
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
